// Decides which bounds the hike map should fit to (or that it should not move).
//
// Extracted from HikeMap.vue's addToMap() so the decision can be unit-tested in
// isolation. The key behaviour it encodes: when `fitBounds` is false the camera
// is left exactly where the user put it. This is what stops a base-map style
// switch (which re-adds all layers via addToMap) from re-zooming back to the
// hike's extent — see the `style.load` / `map-style-loaded` handlers.

export type LngLatBounds = [[number, number], [number, number]];

interface FitBoundsArgs {
  // When false, never move the camera (e.g. re-adding layers after a style switch).
  fitBounds: boolean;
  // Features for the active view (day/route/full filter already applied).
  filteredFeatures: unknown[];
  // 'full' | 'day' | 'route'
  activeViewType: string;
  // [minLng, minLat, maxLng, maxLat] for the whole hike, when available.
  bbox?: number[] | null;
  // All hike features, used as the fallback extent in full view when no bbox.
  allFeatures?: unknown[] | null;
  // Computes a bounds box from a feature list; may return null when it can't.
  computeBoundsFromFeatures: (features: unknown[]) => LngLatBounds | null;
}

// Returns the bounds to pass to map.fitBounds(), or null when the camera should
// stay put (either because fitBounds is disabled or no bounds could be derived).
export function computeHikeMapFitBounds({
  fitBounds,
  filteredFeatures,
  activeViewType,
  bbox,
  allFeatures,
  computeBoundsFromFeatures,
}: FitBoundsArgs): LngLatBounds | null {
  if (!fitBounds) return null;

  // Non-full views (day/route) fit to just the filtered features.
  if (filteredFeatures.length > 0 && activeViewType !== 'full') {
    return computeBoundsFromFeatures(filteredFeatures);
  }

  // Full view resets to the whole-hike extent: prefer the precomputed bbox,
  // otherwise derive it from all features.
  if (activeViewType === 'full') {
    if (bbox && bbox.length === 4) {
      return [
        [bbox[0], bbox[1]],
        [bbox[2], bbox[3]],
      ];
    }
    if (allFeatures && allFeatures.length) {
      return computeBoundsFromFeatures(allFeatures);
    }
  }

  return null;
}
